import type { DsTableRow } from '@gde-mit/angular-design-system';

import type { DocumentListItem } from '@api';

import { formatDocumentFileSize } from './format-document-file-size.util';
import { formatDocumentUploadedAt } from './format-document-uploaded-at.util';

/**
 * Reads a table cell as text. Skips values whose default string form is `[object Object]`.
 *
 * @param value Cell value from a `ds-table` row.
 * @returns The cell text, or an empty string when the value is not a primitive.
 */
export function documentLibraryCellText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return '';
}

/**
 * The list also returns documents a peer shared with the tenant. The API rejects a delete or a
 * visibility change on those (404), and says which rows are the caller's own (`isOwner`). Every
 * document-library surface (list page, attach dialog, bulk actions) must gate delete and visibility
 * mutations on this helper — not on per-screen flags.
 *
 * @returns The result of this operation.
 * @param row The row value.
 */
export function isOwnDocumentRow(row: DsTableRow): boolean {
  return row['isOwnDocument'] === true;
}

/**
 * Maps API list items to `ds-table` rows shared by the library dialog and list page.
 *
 * @returns The result of this operation.
 * @param items The items value.
 */
export function mapDocumentListItemsToTableRows(items: readonly DocumentListItem[]): DsTableRow[] {
  return items.map((item, index) => ({
    id: item.id,
    rowIndex: index,
    fileName: item.fileName,
    fileType: item.fileType.toUpperCase(),
    sizeLabel: formatDocumentFileSize(item.fileSizeBytes),
    uploadedAtLabel: formatDocumentUploadedAt(item.createdAt),
    ownerName: item.ownerName,
    status: item.status,
    visibility: item.visibility,
    isOwnDocument: item.isOwner,
  }));
}
